import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExoAvatar from '../../components/exo/ExoAvatar';
import MoneyLabel from '../../components/label/MoneyLabel';
import DateLabel from '../../components/label/DateLabel';
import ProjectControllingBars from './ProjectControllingBars';
import ProjectControllingDetails from './ProjectControllingDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const metricRenderers = {
  orderVolume: (row, t) => ({
    label: t('Order Volume'),
    value: <MoneyLabel money={row.orderedSumNetto} />,
  }),
  invoiced: (row, t) => ({
    label: t('Invoiced'),
    value: <MoneyLabel money={row.invoicedSumNetto} />,
  }),
  openBillable: (row, t, colors) => ({
    label: t('Time Billing (open)'),
    value: <MoneyLabel money={row.openBillableTimeNetto} />,
    color:
      row.openBillableTimeNetto > 0
        ? colors.money.positive
        : colors.money.neutral,
  }),
  billableByProgress: (row, t, colors) => {
    const workProgress = row.workPackageProgress || 0;
    const billingProgress =
      row.orderedSumNetto > 0
        ? (row.invoicedSumNetto / row.orderedSumNetto) * 100
        : 0;
    const diff = workProgress - billingProgress;
    const amount =
      row.orderedSumNetto > 0 ? (diff / 100) * row.orderedSumNetto : 0;
    return {
      label: t('Performance Billing (open)'),
      value: row.orderedSumNetto > 0 ? <MoneyLabel money={amount} /> : '–',
      color:
        amount > 0
          ? colors.money.positive
          : amount < 0
            ? colors.money.negative
            : colors.money.neutral,
    };
  },
  openBillableHours: (row, t) => ({
    label: t('Open Billable Hours'),
    value: Math.round(row.openBillableTimeSeconds / 3600) + ' h',
  }),
  remainingVolume: (row, t, colors) => {
    const hasOrder = row.orderedSumNetto > 0;
    const remaining = hasOrder
      ? Math.max(0, row.orderedSumNetto - row.invoicedSumNetto)
      : 0;
    return {
      label: t('Remaining Volume'),
      value: hasOrder ? <MoneyLabel money={remaining} /> : '–',
      color: remaining > 0 ? colors.money.positive : colors.money.neutral,
    };
  },
  deadline: (row, t) => ({
    label: t('Deadline'),
    value: row.deadline ? <DateLabel date={row.deadline} /> : '–',
  }),
};

const ProjectControllingCard = ({
  project,
  visibleMetrics,
  isExpanded,
  onToggleExpand,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        backgroundColor: colors.glass,
        borderRadius: '12px',
        backdropFilter: 'blur(12px)',
      }}
      className="p-3"
    >
      {/* Main row */}
      <Box className="flex flex-col md:flex-row items-start gap-3">
        {/* Left: Avatar + Project info */}
        <Box
          className="flex gap-3 items-center cursor-pointer shrink-0 w-full md:w-auto"
          onClick={() => navigate(`/projects/management/${project.id}`)}
          sx={{ width: { md: 220 } }}
        >
          <ExoAvatar
            picture={project.image?.temporaryUrl || project.image?.thumbnailUrl}
            name={project.name}
            size="44px"
            type="company"
          />
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {project.nr}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              noWrap
              title={project.name}
            >
              {project.name}
            </Typography>
            {project.client && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {project.client.name}
                {project.client.city ? ` · ${project.client.city}` : ''}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Center: Metrics */}
        {visibleMetrics.length > 0 && (
          <Box
            className="flex flex-wrap gap-4 items-center"
            sx={{ flex: '0 1 auto', minWidth: 0 }}
          >
            {visibleMetrics.map((key) => {
              const renderer = metricRenderers[key];
              if (!renderer) return null;
              const { label, value, color } = renderer(project, t, colors);
              return (
                <Box key={key} className="flex flex-col">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="monospace"
                    fontWeight="bold"
                    sx={{ color: color || 'inherit', fontSize: '0.85rem' }}
                  >
                    {value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Right: Bars */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <ProjectControllingBars row={project} />
        </Box>

        {/* Expand button */}
        <IconButton
          size="small"
          onClick={onToggleExpand}
          className="hidden md:flex"
        >
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {/* Expandable details */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          className="mt-3 pt-3"
          sx={{ borderTop: `1px solid ${colors.glass}` }}
        >
          <ProjectControllingDetails project={project} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProjectControllingCard;
